import type {
  Cadence,
  SectionDefaultsDto,
  SectionId,
  SectionMetaDto,
} from './reportEnums.constants';

/** Sections with paired Clinical / Financial insight cards (builder toggles only). */
export const SECTIONS_WITH_INSIGHT_CARDS: SectionId[] = [
  'vbp-engagement',
  'what-members-need',
  'connections-behavioral-care',
  'bh-medical-diagnoses',
  'clinical-care-outcomes',
  'connections-primary-care',
];

/**
 * @param sectionId - Library section id.
 * @returns Whether the section uses clinical/financial insight toggles.
 */
export const sectionSupportsInsightCards = (
  sectionId: SectionId,
): boolean => SECTIONS_WITH_INSIGHT_CARDS.includes(sectionId);

/** Order matches the 18-section Customer Report Studio library. */
export const SECTION_LIBRARY: SectionMetaDto[] = [
  {
    id: 'hero',
    title: 'Hero / Cover',
    defaultOrder: 1,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['customerName', 'reportingPeriod', 'kpiTiles'],
    defaults: {},
    rationale: 'Cover slide with headline + top KPI tiles — always on.',
  },
  {
    id: 'engagement-story',
    title: 'Member Engagement',
    defaultOrder: 2,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['engagementFunnel', 'denominatorViews'],
    defaults: {},
    rationale:
      'Narrative anchor — full engagement funnel + three denominator views. Paired Clinical + Financial insights.',
  },
  {
    id: 'vbp-engagement',
    title: 'VBP Engagement',
    defaultOrder: 3,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['vbpFunnel', 'sixMonthGrowth'],
    defaults: { payers: ['UHC'], productConfigs: ['vbp'] },
    rationale:
      'UHC-only default (Rules.md #6). 6-month growth chart + VBP funnel + paired insights.',
  },
  {
    id: 'who-were-reaching',
    title: "Who We're Reaching",
    defaultOrder: 4,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['geoBreakdown', 'ageBreakdown', 'raceEthnicityBreakdown'],
    defaults: {},
    rationale: 'Demographics: counties, age, race/ethnicity of engaged members.',
  },
  {
    id: 'what-members-need',
    title: 'What Members Need',
    defaultOrder: 5,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['topNeeds', 'identifiedVsFulfilled'],
    defaults: {},
    rationale:
      'Top needs + "how a need gets met" 4-step story + Identified vs Fulfilled trend. Paired insights.',
  },
  {
    id: 'connections-that-matter',
    title: 'Connections That Matter',
    defaultOrder: 6,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['topCboConnections', 'topGovProgramConnections'],
    defaults: {},
    rationale:
      'Ranked list of Top CBO & Government Program connections made this cycle.',
  },
  {
    id: 'quality-measure-projections',
    title: 'Quality Measure Projections',
    defaultOrder: 7,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['measureCards'],
    defaults: {
      cadences: ['quarterly'],
      productConfigs: ['quality-measures'],
    },
    rationale:
      'Blood pressure gaps, FUA/FUH/FUM, and VBP engagement projections vs target. Defaults on for quarterly + Quality Measures config.',
  },
  {
    id: 'connections-behavioral-care',
    title: 'Connections to Behavioral Care',
    defaultOrder: 8,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['bhReferrals', 'bhConnections'],
    defaults: { productConfigs: ['behavioral-health'] },
    rationale: 'On only when the customer has a Behavioral Health program.',
  },
  {
    id: 'bh-medical-diagnoses',
    title: 'Behavioral Health and Medical Diagnoses',
    defaultOrder: 9,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['diagnosisBreakdown', 'whatWeFound'],
    defaults: {
      cadences: ['quarterly'],
      productConfigs: ['behavioral-health'],
    },
    rationale:
      'Concrete breakdown with a "What we found" narrative. Quarterly + BH-gated.',
  },
  {
    id: 'clinical-care-outcomes',
    title: 'Clinical Care Outcomes',
    defaultOrder: 10,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['phq9Series', 'gad7Series'],
    defaults: { cadences: ['quarterly'] },
    rationale:
      'PHQ-9 + GAD-7 3-month trend lines with the Minimal threshold band. Quarterly default.',
  },
  {
    id: 'connections-primary-care',
    title: 'Connections to Primary Care',
    defaultOrder: 11,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['pcpConnections', 'membersNoPcpOverYear'],
    defaults: { productConfigs: ['primary-care'] },
    rationale:
      "Defaults on when the customer has Primary Care configured; includes the 'no PCP in over a year' narrative.",
  },
  {
    id: 'harp-enrollment',
    title: 'HARP Enrollment',
    defaultOrder: 12,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['statuses'],
    defaults: {},
    rationale:
      'Guide-tracked HARP enrollment status (Undecided / Enrolled / Refused / In Progress).',
  },
  {
    id: 'gap-closures',
    title: 'Gap Closures',
    defaultOrder: 13,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['matrix'],
    defaults: {},
    rationale:
      'Screening gap-closure matrix (A1C / Dental / Breast / Colon / Kidney) by status.',
  },
  {
    id: 'home-visits',
    title: 'Home Visits',
    defaultOrder: 14,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['rows'],
    defaults: {},
    rationale:
      'Home-visit tracking with MANNA and Scene partners by status.',
  },
  {
    id: 'member-spotlight',
    title: 'Member Spotlight',
    defaultOrder: 15,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['summary', 'needs', 'outcomes'],
    defaults: {},
    rationale:
      'CSM-authored member story (structured Needs / Priorities / Challenge / How Reema helped / Outcomes).',
  },
  {
    id: 'closing-value',
    title: 'The Bottom Line',
    defaultOrder: 16,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['costReductionRows', 'validatedOutcomes'],
    defaults: {},
    rationale:
      'Cost-reduction math (BH + PCP totals) + validated outcomes + closing CTA.',
  },
  {
    id: 'whats-possible-at-maturity',
    title: "What's Possible at Maturity",
    defaultOrder: 17,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['obhWisconsinResults'],
    defaults: {},
    rationale:
      'OBH Wisconsin validated results: 3.2:1 ROI, ↓33% inpatient, ↓12% ED, $395 PMPM.',
  },
  {
    id: 'looking-ahead',
    title: 'Looking Ahead',
    defaultOrder: 18,
    supportsCallout: true,
    supportsNarrative: false,
    dataRequirements: ['nextCycleFocus', 'preparedBy'],
    defaults: {},
    rationale: 'Closing narrative + prepared-by footer.',
  },
];

/**
 * Returns whole months between an ISO engagement start date and a reference date.
 * @param fromIso Engagement start date (YYYY-MM-DD).
 * @param to Reference date (defaults to now).
 * @returns Whole months elapsed.
 */
export function monthsBetween(fromIso: string, to: Date = new Date()): number {
  const from = new Date(fromIso);
  const years = to.getFullYear() - from.getFullYear();
  const months = to.getMonth() - from.getMonth();
  return years * 12 + months;
}

function matchesDefaults(
  defaults: SectionDefaultsDto,
  cadence: Cadence,
): boolean {
  if (defaults.cadences && !defaults.cadences.includes(cadence)) {
    return false;
  }
  return true;
}

/**
 * Whether a section should default to included for the given cadence.
 * Payer, product, and lifecycle gates were removed; users toggle sections in the builder.
 * @param section Library section metadata.
 * @param cadence Report cadence.
 * @returns True when the section defaults to on.
 */
export function isDefaultOn(
  section: SectionMetaDto,
  cadence: Cadence,
): boolean {
  return matchesDefaults(section.defaults, cadence);
}

/** Set of valid section ids for governance checks. */
export const VALID_SECTION_IDS = new Set<SectionId>(
  SECTION_LIBRARY.map(s => s.id),
);
